import { useQuery } from '@apollo/client';
import { Box, CircularProgress } from '@material-ui/core';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';

import { useConfig, useErrorPage, usePromoCodeDetailsFromQuery } from '../../lib/hooks';
import { PromoCodeDetails } from '../../lib/interfaces';
import validateNissanRdr from '../../src/apollo/queries/nissan/validateNissanRdr';
import { validatePromoCodeRequest } from '../../src/helpers/promotionClient';
import { getFailedCodeValidationMessage } from '../PromoCodeEntryExtend';

import _ from 'lodash';
import { extractErrorCode } from '../../src/helpers/extractErrorCode';

export default function NissanCallback() {
  const errorPage = useErrorPage();
  const router = useRouter();
  const promoCodeDetails = usePromoCodeDetailsFromQuery();
  const config = useConfig();
  const [validated, setValidated] = useState(false);

  const { refetch: validateRdr } = useQuery(validateNissanRdr, {
    variables: {
      input: {
        promoCode: promoCodeDetails?.promotionCode,
      },
    },
    skip: true,
  });

  const redirectSuccess = useCallback(
    ({ form, promotionCode, promotionType }: { form: string; promotionCode: string; promotionType: string }) => {
      router.push({
        pathname: `/register`,
        query: { promotionCode, promotionType, form, portal: 'nissan' },
      });
    },
    [router],
  );

  useEffect(() => {
    if (validated || !promoCodeDetails?.promotionCode) return;

    const validate = async () => {
      setValidated(true);

      try {
        const result = await validatePromoCodeRequest({
          promotionCode: promoCodeDetails.promotionCode,
          promotionRef: config.promotion.ref,
        });

        if (!result.success) {
          errorPage('Unknown', 'Error validating promo code, please try again.');
          return;
        }

        const rdrData = await validateRdr();
        const { form } = rdrData.data.validateNissanRdr;

        redirectSuccess({ form, ...(promoCodeDetails as PromoCodeDetails) });
      } catch (error) {
        const message = getFailedCodeValidationMessage(error as Error);
        if (message) return errorPage(extractErrorCode(error), message);
        errorPage(extractErrorCode(error), _.get(error, 'message', 'Unknown'));
      }
    };

    validate();
  }, [config.promotion.ref, errorPage, promoCodeDetails, redirectSuccess, validateRdr, validated]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 18 }}>
      <CircularProgress />
    </Box>
  );
}
